'use client';

import { css } from 'styled-components';
import { whenDisabled, whenFocused, whenHovered, whenPressed } from 'utils';
import { theme } from '@frameio/vapor-theme';
import type { ButtonColor } from './types';

type VariantProps = {
  disabled: boolean;
  $color: ButtonColor;
};

const {
  primary: buttonPrimary,
  secondary: buttonSecondary,
  destructive: buttonDestructive,
  neutral: buttonNeutral,
} = theme.colors.components.button;

export function containedVariant() {
  return css<VariantProps>`
    ${baseStyles};

    ${whenHovered} {
      ${hoverStyles};
    }

    ${whenFocused} {
      ${focusStyles};
    }

    &[aria-expanded='true'],
    ${whenPressed} {
      ${activeStyles};
    }
  `;
}

function baseStyles(p: VariantProps) {
  if (p.$color === 'primary') {
    return css`
      background-color: ${buttonPrimary.background};
      color: ${buttonPrimary.text};
      ${whenDisabled} {
        background-color: ${buttonPrimary.interactive.background.disabled};
        color: ${theme.colors.text.interactive.disabled};
        pointer-events: none;
      }
    `;
  }

  if (p.$color === 'error') {
    return css`
      background-color: ${buttonDestructive.background};
      color: ${buttonPrimary.text};
      ${whenDisabled} {
        background-color: ${buttonDestructive.interactive.background.disabled};
        color: ${theme.colors.text.interactive.disabled};
        pointer-events: none;
      }
    `;
  }

  if (p.$color === 'neutral') {
    return css`
      background-color: ${buttonNeutral.background};
      color: ${theme.colors.text.secondaryStark};
      ${whenDisabled} {
        background-color: ${buttonSecondary.interactive.background.disabled};
        color: ${theme.colors.text.interactive.disabled};
        pointer-events: none;
      }
    `;
  }

  return css`
    background-color: ${buttonSecondary.background};
    color: ${buttonSecondary.text};
    ${whenDisabled} {
      background-color: ${buttonSecondary.interactive.background.disabled};
      color: ${theme.colors.text.interactive.disabled};
      pointer-events: none;
    }
  `;
}

function hoverStyles(p: VariantProps) {
  if (p.$color === 'primary') {
    return css`
      background-color: ${buttonPrimary.interactive.background.hover};
    `;
  }

  if (p.$color === 'error') {
    return css`
      background-color: ${buttonDestructive.interactive.background.hover};
    `;
  }

  if (p.$color === 'neutral') {
    return css`
      background-color: ${buttonNeutral.interactive.background.hover};
    `;
  }

  return css`
    background-color: ${buttonSecondary.interactive.background.hover};
  `;
}

function focusStyles(p: VariantProps) {
  return hoverStyles(p);
}

function activeStyles(p: VariantProps) {
  if (p.$color === 'primary') {
    return css`
      background-color: ${buttonPrimary.interactive.background.pressed};
    `;
  }

  if (p.$color === 'error') {
    return css`
      background-color: ${buttonDestructive.background};
    `;
  }

  if (p.$color === 'neutral') {
    return css`
      background-color: ${buttonNeutral.background};
    `;
  }

  return css`
    background-color: ${buttonSecondary.interactive.background.activated};
  `;
}
